import { useContext, useState } from 'react'

import { useDeleteArticleMutation } from '@/common/api/modules/articles'
import {
  articleCreationSourceBadgeType,
  articleCreationSourceLabel,
  articleStatusBadgeType,
  articleStatusLabel
} from '@/common/app/modules/media/article'
import { articleApprovalStatusLabel } from '@/common/app/modules/media/articleApproval'
import { articleDetailPath, articleNewPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { useShowDeletedToast } from '@/common/app/toast'
import { dateTimeStr } from '@/common/form/date'
import { ArticleProposalListItem } from '@/common/model/media/article'

import Badge from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import IconButton from '@/components/view/atoms/button/IconButton'
import TextButton from '@/components/view/atoms/button/TextButton'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'

import { ArticleDiffModalContext } from '@/components/container/modal/article/ArticleDiffModalProvider'
import { ConfirmDialogContext } from '@/components/container/modal/dialog/confirm/ConfirmDialogProvider'

interface Props {
  item: ArticleProposalListItem
  onChanged(): void
}

// 記事一覧の1行（記事案単位。展開すると配下バージョンを表示）
const ArticleListRow = ({ item, onChanged }: Props) => {
  const siteId = useSiteId()
  const [expanded, setExpanded] = useState(false)
  const { showModal, hide } = useContext(ConfirmDialogContext)
  const { showModal: showArticleDiff } = useContext(ArticleDiffModalContext)
  const showDeleted = useShowDeletedToast()

  const { trigger: deleteArticle } = useDeleteArticleMutation({
    onSuccess: () => {
      onChanged()
      showDeleted('記事')
    }
  })

  const handleDelete = () =>
    showModal({
      message: `「${item.title || '無題の記事'}」を削除しますか？（配下の全バージョンが削除されます）`,
      noText: 'キャンセル',
      yesText: '削除する',
      onClickYes: async () => {
        await deleteArticle({ site_id: siteId, article_proposal_id: item.article_proposal_id })
        hide()
      }
    })

  return (
    <>
      <TableRow noBorder={expanded}>
        <TableRowItem width={40}>
          <IconButton
            onClick={() => setExpanded((v) => !v)}
            type={expanded ? IconType.UP : IconType.DOWN}
          />
        </TableRowItem>
        <TableRowItem>
          <TextButton link={articleDetailPath(siteId, item.article_proposal_id)} bold>
            {item.title || '（無題）'}
          </TextButton>
        </TableRowItem>
        <TableRowItem width={128}>
          <Text size={14}>{`#${item.article_proposal_id}`}</Text>
        </TableRowItem>
        <TableRowItem width={128}>
          <Badge size={12} badgeType={articleCreationSourceBadgeType(item.creation_source)}>
            {articleCreationSourceLabel(item.creation_source)}
          </Badge>
        </TableRowItem>
        <TableRowItem width={160}>
          <Badge size={10} badgeType={articleStatusBadgeType(item.status)}>
            {articleStatusLabel(item.status)}
          </Badge>
        </TableRowItem>
        <TableRowItem width={160}>
          <Text size={14}>{item.updated_at ? dateTimeStr(item.updated_at) : '-'}</Text>
        </TableRowItem>
        <TableRowItem width={160}>
          <div className='flex items-center gap-1'>
            <Button
              link={`${articleNewPath(siteId)}?copiedId=${item.article_proposal_id}`}
              color={ButtonColor.SECONDARY}
              size='icon'
              square
            >
              <Icon type={IconType.COPY_PLUS} color={IconColor.SECONDARY} />
            </Button>
            <Button color={ButtonColor.SECONDARY} size='icon' square onClick={handleDelete}>
              <Icon type={IconType.TRASH} color={IconColor.CRITICAL} />
            </Button>
          </div>
        </TableRowItem>
      </TableRow>
      {expanded && (
        <>
          <tr className='border-surface border-r'>
            <th />
            <TableHeadItem radius='first' type='thin' background='tertiary'>
              <Text size={12} color={TextColor.TERTIARY}>
                バージョン
              </Text>
            </TableHeadItem>
            <TableHeadItem type='thin' background='tertiary'>
              <Text size={12} color={TextColor.TERTIARY}>
                承認リクエストID
              </Text>
            </TableHeadItem>
            <TableHeadItem type='thin' background='tertiary'>
              <Text size={12} color={TextColor.TERTIARY}>
                担当者
              </Text>
            </TableHeadItem>
            <TableHeadItem type='thin' background='tertiary'>
              <Text size={12} color={TextColor.TERTIARY}>
                アップロードステータス
              </Text>
            </TableHeadItem>
            <TableHeadItem type='thin' background='tertiary'>
              <Text size={12} color={TextColor.TERTIARY}>
                アップロード予定日時
              </Text>
            </TableHeadItem>
            <TableHeadItem radius='last' type='thin' background='tertiary'>
              <Text size={12} color={TextColor.TERTIARY}>
                操作
              </Text>
            </TableHeadItem>
          </tr>
          {item.versions.map((v) => (
            <TableRow key={v.id} noBorder>
              <TableRowItem width={40}>{null}</TableRowItem>
              <TableRowItem
                background='secondary'
                radius='first'
                multipleItems={[
                  <Text size={12} weight='bold'>
                    v{v.version_number}
                  </Text>,
                  <TextButton
                    size={12}
                    onClick={() =>
                      showArticleDiff({
                        siteId,
                        articleProposalId: item.article_proposal_id,
                        articleId: v.id
                      })
                    }
                  >
                    更新内容確認
                  </TextButton>
                ]}
              ></TableRowItem>

              <TableRowItem background='secondary' radius='middle' width={128}>
                <Text size={12} color={TextColor.SECONDARY}>
                  {v.upload ? `#${v.upload.request_id}` : '-'}
                </Text>
              </TableRowItem>
              <TableRowItem background='secondary' radius='middle' width={160}>
                <Text size={12} color={TextColor.SECONDARY}>
                  {v.upload?.requested_by_name || '-'}
                </Text>
              </TableRowItem>
              <TableRowItem background='secondary' radius='middle' width={160}>
                <Text size={12} color={TextColor.SECONDARY}>
                  {v.upload ? articleApprovalStatusLabel(v.upload.status) : '-'}
                </Text>
              </TableRowItem>
              <TableRowItem background='secondary' radius='middle' width={160}>
                <Text size={12} color={TextColor.SECONDARY}>
                  {v.upload?.uploaded_at
                    ? dateTimeStr(v.upload.uploaded_at)
                    : v.upload?.scheduled_upload_at
                      ? dateTimeStr(v.upload.scheduled_upload_at)
                      : '-'}
                </Text>
              </TableRowItem>
              <TableRowItem background='secondary' radius='last' width={128}>
                <Text size={12} color={TextColor.SECONDARY}>
                  {v.slug || '-'}
                </Text>
              </TableRowItem>
            </TableRow>
          ))}
        </>
      )}
    </>
  )
}

export default ArticleListRow
